import React, { FunctionComponent, useEffect, useMemo, useState } from "react"
import { useHistory, useRouteMatch } from "react-router"
import FormInput from "../../../components/Form/Inputs/FormInput"
import SelectInput from "../../../components/Form/Select/Select"
import Loader from "../../../components/Loader/Loader"
import Seo from "../../../components/Seo/Seo"
import {
  iIdParams,
  iInspectorProps,
  iResponse,
} from "../../../definitions/interfaces"
import { iRoles } from "../../../definitions/Roles"
import {
  get,
  handleDeleteData,
  handleUpdateData,
} from "../../../store/data/actions"
import { useSelector } from "../../../store"
import { notificationOptions, roleGroups } from "../../../api/Definitions"
import { Controller, FormProvider, useForm } from "react-hook-form"
import { store } from "react-notifications-component"
import RolesHeader from "../Header"
import { ItemWithAccess } from "../../../definitions/types"
import { iAccess } from "../../../definitions/Access"
import { unstable_batchedUpdates } from "react-dom"

const Inspector: FunctionComponent<iInspectorProps<iRoles>> = ({
  access,
  item,
  reload,
}) => {
  const [isLoading, setLoading] = useState(false)
  const { permissions } = useSelector(({ permissions }) => ({
    permissions,
  }))
  const history = useHistory()

  const methods = useForm<iRoles>({
    defaultValues: item,
    shouldUnregister: false,
  })

  useEffect(() => {
    methods.reset(item)
  }, [item])

  const {
    formState: { dirtyFields },
  } = methods

  const onSubmit = (data: iRoles) => {
    setLoading(true)
    handleUpdateData<iRoles>({
      data,
      fields: dirtyFields as Partial<iRoles>,
      link: "/v1/roles",
    })
      .then(() => {
        setLoading(false)
        reload()
        store.addNotification({
          title: "Success!",
          message: "Role successfully updated!",
          // message: "teodosii@react-notifications-component",
          type: "success",
          ...notificationOptions,
        })
      })
      .catch((e) => {
        if (e) {
          if (e.reason === "Nothing changed!") {
            store.addNotification({
              message: e.reason,
              type: "warning",
              ...notificationOptions,
            })
          } else {
            store.addNotification({
              message: e.reason,
              type: "danger",
              ...notificationOptions,
            })
          }
        } else {
          store.addNotification({
            message: "Something went wrong!",
            type: "danger",
            ...notificationOptions,
          })
        }
        setLoading(false)
      })
  }

  const onDelete = () => {
    setLoading(true)
    if (confirm("Are you sure you want to delete this?")) {
      handleDeleteData({
        link: "/v1/roles/" + item._id,
      })
        .then(() => {
          setLoading(false)
          store.addNotification({
            title: "Success!",
            message: "Role successfully deleted!",
            // message: "teodosii@react-notifications-component",
            type: "success",
            ...notificationOptions,
          })
          history.goBack()
        })
        .catch((e) => {
          if (e) {
            if (e.reason === "Nothing changed!") {
              store.addNotification({
                message: e.reason,
                type: "warning",
                ...notificationOptions,
              })
            } else {
              store.addNotification({
                message: e.reason,
                type: "danger",
                ...notificationOptions,
              })
            }
          } else {
            store.addNotification({
              message: "Something went wrong!",
              type: "danger",
              ...notificationOptions,
            })
          }
          setLoading(false)
        })
    }
  }

  const invokeSubmit = () => {
    methods.handleSubmit((data) => {
      onSubmit(data as ItemWithAccess<iRoles>)
    })()
  }

  return (
    <FormProvider {...methods}>
      <Seo title="Role Inspector" />
      {isLoading ? (
        <Loader />
      ) : (
        <>
          <RolesHeader
            access={access}
            submitDeleteText={"Delete"}
            submitText={"Update"}
            handleSubmit={invokeSubmit}
            handleDelete={onDelete}
          />
          <div className="inspector">
            <form
              onSubmit={methods.handleSubmit((data) => {
                onSubmit(data as ItemWithAccess<iRoles>)
              })}
              className="inspector-form"
            >
              <FormInput name={"name"} label={"Name"} />
              <Controller
                as={SelectInput}
                control={methods.control}
                name="group"
                label="Group"
                isMulti={false}
                // initialValue={item.group}
                options={roleGroups}
              />
              <Controller
                as={SelectInput}
                control={methods.control}
                name="permissions"
                label="Permissions"
                isMulti={true}
                // initialValue={item.permissions}
                options={permissions}
              />
              <button type="submit" className={"btn-blue d-none"}>
                Save Changes
              </button>
            </form>
          </div>
        </>
      )}
    </FormProvider>
  )
}

const InspectorLoader: FunctionComponent = () => {
  const [isLoading, setLoading] = useState(true)
  const [access, setAccess] = useState<iAccess>()
  const [item, setItem] = useState<iRoles>()
  const match = useRouteMatch<iIdParams>()
  const history = useHistory()

  const loadRoles = () => {
    console.log(match.params._id)

    Promise.all([
      get<iResponse<iAccess>>({
        link: "/v1/access",
        query: { _id: match.params._id, collection: "Roles" },
      }),
      get<iResponse<iRoles>>({ link: "/v1/roles/" + match.params._id }),
    ])
      .then((result) => {
        const [accessResult, dataResult] = result
        unstable_batchedUpdates(() => {
          setItem(dataResult.data)
          setAccess(accessResult.data)
          setLoading(false)
        })
      })
      .catch((err) => {
        if (err?.status === 403) {
          history.replace("/no-permission")
        }
      })
  }

  useMemo(() => {
    loadRoles()
  }, [])

  return isLoading ? (
    <Loader />
  ) : item && access ? (
    <Inspector item={item} access={access} reload={loadRoles} />
  ) : null
}

export default InspectorLoader
